import React, { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Card,
  CardHeader,
  Text,
  Button,
  Badge,
  Spinner,
  makeStyles,
  tokens,
  Title3,
  Caption1,
  Body1,
  Divider,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  PlugDisconnectedRegular,
  PowerRegular,
  InfoRegular,
  CalendarRegular,
  DesktopRegular,
  GlobeRegular,
} from '@fluentui/react-icons';
import { useSession, useSessionStatus, useSessions } from '@/hooks/useSessionQueries';
import { SessionState } from '@/types/api';
import { DisconnectDialog } from '@/components/DisconnectDialog';
import { formatDateTime } from '@/utils/dateFormatters';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  card: {
    padding: tokens.spacingVerticalL,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
  statusSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: tokens.spacingVerticalM,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: tokens.spacingVerticalM,
  },
});

const getStatusColor = (state: SessionState): "success" | "warning" | "danger" | "important" => {
  switch (state) {
    case SessionState.Active:
      return "success";
    case SessionState.Disconnected:
    case SessionState.UserDisconnected:
      return "warning";
    case SessionState.LogOff:
      return "danger";
    default:
      return "important";
  }
};

export const SessionDetails: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { sessionId } = useParams({ from: '/sessions/$sessionId' });
  const [showLogOffDialog, setShowLogOffDialog] = useState(false);
  
  const { data: session, isLoading, error } = useSession(sessionId);
  const { data: allSessions } = useSessions();
  const { data: status } = useSessionStatus(sessionId, !!session && !error);

  // If session not found, try to find it in the current sessions list
  const currentSession = session || allSessions?.find(s => s.id === sessionId);

  const handleLogOff = () => {
    setShowLogOffDialog(true);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <Text size={400}>Loading session details...</Text>
        </div>
      </div>
    );
  }

  if (error || !currentSession) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <PlugDisconnectedRegular style={{ fontSize: '48px', color: tokens.colorPaletteRedForeground1 }} />
          <Text size={500} weight="semibold">Session Not Found</Text>
          <Text size={300}>The session could not be loaded or does not exist.</Text>
          <Button 
            appearance="primary" 
            icon={<ArrowLeftRegular />}
            onClick={() => navigate({ to: '/sessions' })}
          >
            Back to Sessions
          </Button>
        </div>
      </div>
    );
  }

  // Allow logoff for both Active and Disconnected sessions (force cleanup of disconnected sessions)
  const canLogOff = currentSession.sessionState === SessionState.Active ||
                    currentSession.sessionState === SessionState.Disconnected ||
                    currentSession.sessionState === SessionState.UserDisconnected;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate({ to: '/sessions' })}
        >
          Back to Sessions
        </Button>
      </div>

      <Card className={styles.card}>
        <CardHeader
          header={
            <div>
              <Title3>Session Details</Title3>
              <Caption1>{currentSession.id}</Caption1>
            </div>
          }
          action={
            <Badge
              appearance="filled"
              color={getStatusColor(currentSession.sessionState)}
              size="large"
            >
              {currentSession.sessionState}
            </Badge>
          }
        />

        {status && (
          <div className={styles.statusSection}>
            <InfoRegular />
            <Body1>
              Session is {status.isActive ? 'currently active' : 'not active'}
              {status.lastActivityTime && ` • Last activity: ${formatDateTime(status.lastActivityTime)}`}
            </Body1>
          </div>
        )}

        <Divider />

        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <div className={styles.detailHeader}>
              <DesktopRegular />
              <Caption1>Host Pool</Caption1>
            </div>
            <Text weight="semibold">{currentSession.hostPoolName}</Text>
          </div>

          <div className={styles.detailItem}>
            <div className={styles.detailHeader}>
              <DesktopRegular />
              <Caption1>Session Host</Caption1>
            </div>
            <Text weight="semibold">{currentSession.sessionHostName}</Text>
          </div>

          <div className={styles.detailItem}>
            <div className={styles.detailHeader}>
              <CalendarRegular />
              <Caption1>Start Time</Caption1>
            </div>
            <Text weight="semibold">{formatDateTime(currentSession.startTime)}</Text>
          </div>

          {currentSession.lastActivityTime && (
            <div className={styles.detailItem}>
              <div className={styles.detailHeader}>
                <CalendarRegular />
                <Caption1>Last Activity</Caption1>
              </div>
              <Text weight="semibold">{formatDateTime(currentSession.lastActivityTime)}</Text>
            </div>
          )}

          <div className={styles.detailItem}>
            <div className={styles.detailHeader}>
              <DesktopRegular />
              <Caption1>Application Type</Caption1>
            </div>
            <Text weight="semibold">{currentSession.applicationType}</Text>
          </div>

          {currentSession.clientIPAddress && (
            <div className={styles.detailItem}>
              <div className={styles.detailHeader}>
                <GlobeRegular />
                <Caption1>Client IP</Caption1>
              </div>
              <Text weight="semibold">{currentSession.clientIPAddress}</Text>
            </div>
          )}
        </div>

        <Divider />

        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={<PowerRegular />}
            onClick={handleLogOff}
            disabled={!canLogOff}
          >
            Log Off Session
          </Button>
        </div>

        {!canLogOff && (
          <Caption1 style={{ marginTop: tokens.spacingVerticalS }}>
            Log off is disabled because the session is in a {currentSession.sessionState} state.
          </Caption1>
        )}
      </Card>

      {showLogOffDialog && (
        <DisconnectDialog
          sessionId={currentSession.id}
          sessionName={`${currentSession.hostPoolName} - ${currentSession.sessionHostName}`}
          action="logoff"
          onClose={() => setShowLogOffDialog(false)}
        />
      )}
    </div>
  );
};